"use client";

import { useEffect, useState } from "react";

import {
  filterCohorts,
  generateDurationFilter,
  generateStartDateFilter,
  getAllInventoryItems,
  handleCohortFilterChange,
  logError,
  resetCohortFilters,
  setCatalogueItems as setCatalogueItemsStore,
} from "@up/data";

import type { FilterType, TCatalogueItem, TInventoryItem } from "@up/data";
import type { DataTypes } from "@up/form";
import type { ReactNode } from "react";

const generateFilters = function (inventoryItems: TInventoryItem[]) {
  const enabledFilters = {
    startDate: true,
    duration: true,
    course: false,
  };

  return [
    generateStartDateFilter({
      enabledFilters,
      inventoryItems,
    }),
    generateDurationFilter({
      enabledFilters,
      inventoryItems,
    }),
  ];
};

const generateFormData = function (catalogueItems: TCatalogueItem[]) {
  return catalogueItems.map((item) => {
    return {
      value: item._id,
      text: item.name,
      elements: [
        {
          type: "radio",
          name: `cohort-selection-${item._id}`,
          validate: {
            presence: true,
          },
          dependent: {
            dependentName: "program-selection",
            condition: "includes",
            conditionValue: item._id,
          },
          meta: {
            type: "cohort",
          },
          options: item.cohorts.map((ii) => {
            return {
              text: ii.name,
              value: ii._id,
            };
          }),
        } as DataTypes.Radio,
      ],
    };
  });
};

//DataTypes.ProgramSelectionOption[]
export function FetchCatalogueItems({
  children,
}: {
  children: (props: {
    didError: boolean;
    filters: FilterType[];
    handleFilterToggle: (filterName: string, optionName: string) => void;
    isLoading: boolean;
    resetFilters: () => void;
    formData: {
      value: string;
      text: string;
      elements: DataTypes.Radio[];
    }[];
  }) => ReactNode;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [didError, setDidError] = useState(false);
  const [catalogueItems, setCatalogueItems] = useState<TCatalogueItem[]>([]);
  const [filters, setFilters] = useState(() => {
    return generateFilters([]);
  });

  useEffect(function () {
    const go = async function () {
      setIsLoading(true);
      try {
        const _ci = await getAllInventoryItems();
        const ci = _ci.sort((a: TCatalogueItem, b: TCatalogueItem) =>
          a.name.localeCompare(b.name),
        );
        setCatalogueItems(ci);
        setCatalogueItemsStore(ci);
        setFilters(generateFilters(ci.map((c) => c.cohorts).flat()));
      } catch (err) {
        setDidError(true);
        logError({ action: "APPLICATION_FETCH_CATALOGUE", error: err });
      }
      setIsLoading(false);
    };
    go();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterToggle = function (filterName: string, optionName: string) {
    const newFilters = handleCohortFilterChange(
      filters,
      filterName,
      optionName,
    );
    setFilters(newFilters);
  };

  const formData = generateFormData(
    structuredClone(catalogueItems)
      .map((c) => {
        const cohorts = filterCohorts([...c.cohorts], filters);
        c.cohorts = cohorts;
        return c;
      })
      .filter((c) => {
        return c.cohorts.length > 0;
      }),
  );

  const resetFilters = function () {
    setFilters(resetCohortFilters(filters));
  };

  return children({
    didError,
    filters,
    formData,
    handleFilterToggle,
    isLoading,
    resetFilters,
  });
}
